/*=========================================================================================
  File Name: main.js
  Description: Main Vue 3 Entry File (Cleaned & Patched)
==========================================================================================*/

import { createApp, configureCompat } from 'vue';
import Vue from 'vue';

// 1. Configuração de Compatibilidade
configureCompat({
  MODE: 2,
  RENDER_FUNCTION: true,
  FILTERS: true,
  INSTANCE_ATTRS_CLASS_STYLE: true,
  INSTANCE_LISTENERS: true,
  INSTANCE_SCOPED_SLOTS: true,
  INSTANCE_CHILDREN: true,
  COMPONENT_V_MODEL: true,
  // Os ícones do @lucide/vue são funções puras (componente funcional do Vue 3).
  // Com esta flag ligada o compat as trata como async component do Vue 2 e a
  // renderização quebra. Async component no estilo Vue 2 não é usado no código vivo.
  COMPONENT_ASYNC: false,
});

// 2. Shim para Vue.util (VeeValidate Legacy)
if (!Vue.util) {
  Vue.util = {
    warn: console.warn,
    defineReactive: (obj, key, val) => {
      if (Vue.set) {
        Vue.set(obj, key, val);
      } else {
        obj[key] = val;
      }
    },
  };
}

import App from './App.vue';
import Vuesax from 'vuesax';
import 'material-icons/iconfont/material-icons.css';
import 'vuesax/dist/vuesax.css';
import '../themeConfig.js';
import './assets/scss/main.scss';
import '@/assets/css/tailwind.css';
import { BpLoadingPlugin } from '@/components/ui/loadingService.js';
import { BpNotifyPlugin } from '@/components/ui/notifyService.js';

import ECharts from 'vue-echarts';
import 'echarts';

import 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'flatpickr/dist/flatpickr.css';
require('./assets/css/iconfont.css');

import { VueHammer } from 'vue2-hammer';
import { validateLegacy } from './utils/legacyValidator';
import VueMask from 'v-mask';
import VueCurrencyFilter from 'vue-currency-filter';
import VueSignaturePad from 'vue-signature-pad';
import VueTheMask from 'vue-the-mask';
import formatDate from './utils/pipe/formatDate';
import VueScrollTo from 'vue-scrollto';
import { VTooltip, VPopover, VClosePopover } from 'v-tooltip';
import VueKonva from 'vue-konva';
import { abilitiesPlugin } from '@casl/vue';
import ability from './services/ability';
import router from './router';
import store from './store/store';
import i18n from './i18nInstance';
import registerGlobalComponents from './globalComponents';
import registerFilters from './filters/filters';
import { convertLabelsToTranslate } from './services/translate/translateMapper.js';

const app = createApp(App);

app.use(router);
app.use(store);
app.use(i18n);
app.use(Vuesax);
app.use(VueHammer);
app.use(VueMask);
app.use(VueCurrencyFilter, {
  symbol: 'R$',
  thousandsSeparator: '.',
  fractionCount: 2,
  fractionSeparator: ',',
  symbolPosition: 'front',
  symbolSpacing: true,
});
app.use(VueSignaturePad);
app.use(VueTheMask);
app.use(formatDate);
app.use(VueScrollTo);
app.use(VueKonva);
// Expõe `this.$bpLoading` — substituto do `$vs.loading` (BPS-684, etapa V2-17).
app.use(BpLoadingPlugin);
// Expõe `this.$bpNotify` — substituto do `$vs.notify` (BPS-684, etapa V2-8).
app.use(BpNotifyPlugin);

app.use(abilitiesPlugin, ability.getPermission(), {
  useGlobalProperties: true,
});
app.config.globalProperties.$actionsDefault = ability.actionsDefault;

app.component('v-chart', ECharts);
app.component('v-popover', VPopover);
app.directive('tooltip', VTooltip);
app.directive('close-popover', VClosePopover);

if (typeof registerGlobalComponents === 'function')
  registerGlobalComponents(app);
if (typeof registerFilters === 'function') registerFilters(app);

try {
  const cached = localStorage.getItem('companyTranslations');
  if (cached) {
    const msgs = convertLabelsToTranslate(JSON.parse(cached)) || {};
    i18n.global.setLocaleMessage('default', msgs);
    i18n.global.locale = 'default';
  }
} catch {
  console.warn('Falha ao realizar traduções!');
}

app.mixin({
  data() {
    return {
      veeFields: {},
      veeErrors: {
        has: () => false,
        first: () => null,
        all: () => [],
        collect: () => [],
        any: () => false,
        add: () => {},
        remove: () => {},
        clear: () => {},
      },
      errors: {
        has: () => false,
        any: () => false,
        first: () => null,
        all: () => [],
      },
    };
  },
  beforeCreate() {
    this.$set = (target, key, value) => {
      target[key] = value;
      return value;
    };
    this.$delete = (target, key) => {
      delete target[key];
    };
  },
  methods: {
    hasOwnProperty(prop) {
      return this ? Object.prototype.hasOwnProperty.call(this, prop) : false;
    },
  },

  computed: {
    $validator() {
      const self = this;

      return {
        errors: self.errors,
        veeErrors: self.veeErrors,

        validateAll: async () => {
          if (!self.validationSchema || !self.form) return true;

          const errors = await validateLegacy(self.validationSchema, self.form);

          Object.assign(self.veeErrors, {
            has: (field) => !!errors[field],
            first: (field) => errors[field] || null,
            all: () => Object.values(errors),
            collect: () => Object.values(errors),
            any: () => Object.keys(errors).length > 0,
          });

          Object.assign(self.errors, {
            has: (field) => !!errors[field],
            first: (field) => errors[field] || null,
            all: () => Object.values(errors),
            any: () => Object.keys(errors).length > 0,
          });

          return Object.keys(errors).length === 0;
        },

        reset: () => {
          Object.assign(self.veeErrors, {
            has: () => false,
            first: () => null,
            all: () => [],
            collect: () => [],
            any: () => false,
          });

          Object.assign(self.errors, {
            has: () => false,
            first: () => null,
            all: () => [],
            any: () => false,
          });
        },
        // TODO: Passar a utlizar validateAll ao inves de validate
        validate: async () => {
          return self.$validator.validateAll();
        },

        flag: () => {},
        pause: () => {},
        resume: () => {},
      };
    },
  },
  created() {
    patchVNodeTag(this);
  },
  beforeUpdate() {
    cleanChildren(this);
  },
  updated() {
    patchVNodeTag(this);
    cleanChildren(this);
  },
  mounted() {
    cleanChildren(this);
    if (this.$vnode && typeof this.$vnode.tag !== 'string') {
      const safeTag =
        this.$options._componentTag ||
        this.$options.name ||
        (this.$vnode.type ? this.$vnode.type.name : '') ||
        'div';
      try {
        this.$vnode.tag = safeTag;
      } catch (e) {}
    }
  },
});

function patchVNodeTag(instance) {
  if (!instance || !instance.$vnode) return;
  const tag = instance.$vnode.tag;
  if (tag && typeof tag !== 'string' && typeof tag.indexOf !== 'function') {
    try {
      Object.defineProperty(tag, 'indexOf', {
        value: function (searchString) {
          const name =
            instance.$options.name || instance.$options._componentTag || '';
          return name.indexOf(searchString);
        },
        writable: true,
        configurable: true,
      });
    } catch (e) {}
  }
}

function cleanChildren(instance) {
  try {
    const children = instance.$children;
    if (children && Array.isArray(children)) {
      for (let i = children.length - 1; i >= 0; i--) {
        if (!children[i]) children.splice(i, 1);
      }
    }
  } catch (e) {}
}

// ERROR HANDLER
app.config.errorHandler = (err, vm, info) => {
  if (
    err instanceof TypeError &&
    (err.message.includes("reading 'value'") ||
      err.message.includes('focusValue'))
  ) {
    return;
  }
  console.error('[Vue ErrorHandler]:', err, info);
};

app.mount('#app');
